import { Component, Input, OnInit, inject } from '@angular/core';
import { Capture } from '../../../core/models/capture.model';
import { CaptureService } from '../../../core/services/capture.service';
import { PatientMatchStatus } from '../../../core/enums/patient-match-status.enum';
import { NavigationService } from '../../../core/services/navigation.service';
import { CaptureQueueService } from '../../../core/services/capture-queue.service';
import { CaptureQueueFilters } from '../../../core/models/user-settings/capture-queue-settings.model';
import { NextResult } from '../../../core/models/paged-results/cursor-based-paging.model';


@Component({
    selector: 'app-needs-patient-match-approval',
    templateUrl: './needs-patient-match-approval.component.html',
    styleUrls: ['./needs-patient-match-approval.component.scss'],
    imports: []
})
export class NeedsPatientMatchApprovalComponent implements OnInit {
  private captureService = inject(CaptureService);
  private captureQueueService = inject(CaptureQueueService);
  private navigationService = inject(NavigationService);

  @Input() capture: Capture;

  approvalType = null;
  matchName = '';
  matchName2 = '';
  hasMultipleApprovals = false;
  isFirstType = false;
  submitting = false;

  nextCaptureResult: NextResult;

  ngOnInit() {
    this.init();
    this.prefetchNextCapture();
  }

  onRejectClick() {
    this.processPatientMatch(PatientMatchStatus.rejected);
  }

  onApproveClick() {
    this.processPatientMatch(PatientMatchStatus.approved);
  }

  private init() {
    const patientMatchData = this.capture.patientMatchApproval;
    const nameMatchApprovals = patientMatchData.needsApproval || [];

    if (nameMatchApprovals.length > 0) {
      this.approvalType = nameMatchApprovals[0];
      this.hasMultipleApprovals = nameMatchApprovals.length > 1;
      this.isFirstType = this.approvalType === 'first';

      const patientProperty = this.approvalType + 'Name';
      const candidateProperty =
        'patient' + this.approvalType[0].toUpperCase() + this.approvalType.substr(1).toLowerCase();
      const nameMatchData = patientMatchData[this.approvalType + 'MatchData'];
      if (nameMatchData.matchNamePatient) {
        // New captures will include matchName and matchModel information
        this.matchName = nameMatchData.matchNamePatient;
        this.matchName2 = nameMatchData.matchNameClaim;
      } else {
        this.matchName = this.capture.patient[patientProperty];
        this.matchName2 = this.capture.candidate[candidateProperty];
      }
    }
  }

  private processPatientMatch(status: PatientMatchStatus) {
    if (this.submitting) {
      return;
    }

    this.submitting = true;

    this.captureService.processPatientMatch(this.capture, status, this.approvalType).subscribe({
      next: () => {
        // Leave the buttons locked until we navigate away or the component reloads
        this.goToNextCapture(status);
      },
      error: err => {
        this.submitting = false;
        throw err;
      },
    });
  }

  private prefetchNextCapture() {
    const additionalFilters = new CaptureQueueFilters();
    additionalFilters.excludedPatientIds = [this.capture.patient.id];
    this.captureQueueService.getNextResult(this.capture, additionalFilters).subscribe((nextResult: NextResult) => {
      this.nextCaptureResult = nextResult;
    });
  }

  private goToNextCapture(status) {
    // If this capture needs multiple approvals, show the next approval for it
    if (this.hasMultipleApprovals && status === PatientMatchStatus.approved) {
      this.navigationService.reloadComponent();
    } else if (this.nextCaptureResult) {
      this.navigationService.navigateTo(`/capture-admin/captures/${this.nextCaptureResult.id}`);
    } else {
      this.navigationService.navigateTo(`/capture-admin/captures`);
    }
  }
}
